'use client'
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import { format } from 'date-fns'
import EarningCalendar from './earning-calendar'

const today = new Date()

const currentDay = today.getDate()

const isAfterCycleEnd = currentDay > 21

const cycleStartCalendar = isAfterCycleEnd
  ? new Date(today.getFullYear(), today.getMonth(), 22)
  : new Date(today.getFullYear(), today.getMonth() - 1, 22)

const cycleEndCalendar = isAfterCycleEnd
  ? new Date(today.getFullYear(), today.getMonth() + 1, 21)
  : new Date(today.getFullYear(), today.getMonth(), 21)

const todayDate = format(today, 'MMM dd')
const cycleStartDate = format(cycleStartCalendar, 'MMM dd')
const cycleEndDate = format(cycleEndCalendar, 'MMM dd')
const payDayDate = format(
  new Date(
    today.getFullYear(),
    today.getMonth() + (isAfterCycleEnd ? 2 : 1),
    7
  ),
  'MMM dd'
)

export default function Calendar({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const [mobileModalOpen, setMobileModalOpen] = useState(isOpen)
  const [modalLargeOpen, setModalLargeOpen] = useState(isOpen)

  const isMobile = useBreakpointValue({ base: true, sm: false })

  const handleCloseLargeModal = () => {
    setModalLargeOpen(false)
    onClose()
  }

  const handleCloseMobileModal = () => {
    setMobileModalOpen(false)
    onClose()
  }

  useEffect(() => {
    if (isMobile) {
      setMobileModalOpen(isOpen)
      setModalLargeOpen(false)
    } else {
      setModalLargeOpen(isOpen)
      setMobileModalOpen(false)
    }
  }, [isMobile, isOpen])

  return (
    <>
      <Modal
        isOpen={modalLargeOpen}
        onClose={handleCloseLargeModal}
        isCentered
        size={'2xl'}
      >
        <ModalOverlay />
        <ModalContent mx={18} borderRadius={'16px !important'}>
          <ModalHeader
            py={5}
            className="flex items-center justify-between border-b-2 border-b-[#9F9F9F30] px-4"
          >
            <Text className="text-[18px] text-[#727272]">
              Earnings and wallet cycles
            </Text>{' '}
            <ModalCloseButton
              color={'#656565'}
              position={'unset'}
              fontSize={14}
            />
          </ModalHeader>
          <ModalBody p={0} borderTopRadius={28}>
            <Flex className="mt-5 flex-col justify-center px-4">
              <Flex className="flex-col items-center rounded-3xl bg-[#24BFA3] px-2 pb-2">
                <Text className="mb-3 mt-4 text-center text-lg font-semibold text-white">
                  Current Cycle: {format(cycleStartCalendar, 'MMM dd, yyyy')} -{' '}
                  {format(cycleEndCalendar, 'MMM dd, yyyy')}
                </Text>
                <EarningCalendar
                  defaultRange={{
                    startDate: cycleStartCalendar,
                    endDate: cycleEndCalendar,
                  }}
                />
              </Flex>

              <Flex justifyContent={'space-between'} px={8}>
                <Flex className="my-3 flex-col text-center text-[#656565]">
                  <Text className="text-sm">Today’s Date</Text>
                  <Text className="text-xl font-semibold">{todayDate}</Text>
                </Flex>

                <Flex className="my-3 flex-col text-center text-[#656565]">
                  <Text className="text-sm">Cycle Start</Text>
                  <Text className="text-xl font-semibold">
                    {cycleStartDate}
                  </Text>
                </Flex>

                <Flex className="my-3 flex-col text-center text-[#656565]">
                  <Text className="text-sm">Cycle End</Text>
                  <Text className="text-xl font-semibold">{cycleEndDate}</Text>
                </Flex>

                <Flex className="my-3 flex-col text-center">
                  <Text className="text-sm text-[#656565]">Payday</Text>
                  <Text className="text-xl font-semibold text-[#24BFA3]">
                    {payDayDate}
                  </Text>
                </Flex>
              </Flex>

              <Text
                lineHeight={'normal'}
                className="mb-20 text-[16px] text-[#656565]"
              >
                LYKSTAGE wallet statement closes on the 21st of every month,
                which means one cycle is 22nd of the previous month to the 21st
                of the current month, settle on the 7th of the following month.
              </Text>
            </Flex>

            <Flex className="absolute bottom-[4%] right-0 flex-col gap-3 px-4">
              <Button
                className="justify-end py-2 font-semibold"
                color={'white'}
                px={14}
                bg={'lyk-green'}
                border="2px solid white"
                _hover={{
                  border: '2px solid white',
                }}
                onClick={handleCloseLargeModal}
              >
                Got it
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* MOBILE VIEW */}
      <Box>
        <Modal
          isCentered={false}
          onClose={handleCloseMobileModal}
          isOpen={mobileModalOpen}
          motionPreset="slideInBottom"
        >
          <ModalOverlay />
          <ModalContent
            borderTopRadius={28}
            position="fixed"
            pb={84}
            bottom="0"
            margin="0 auto"
            width="100%"
            maxWidth="600px"
            display={{ base: 'block', sm: 'none' }}
          >
            <ModalHeader
              py={5}
              className="flex items-center justify-between border-b-2 border-b-[#9F9F9F30] px-4"
            >
              <Text className="text-[18px] text-[#727272]">
                Earnings are based on ads
              </Text>{' '}
              <ModalCloseButton
                color={'#656565'}
                position={'unset'}
                fontSize={14}
              />
            </ModalHeader>
            <ModalBody p={0} borderTopRadius={28}>
              <Flex className="mt-5 flex-col justify-center px-4">
                <Flex className="flex-col items-center rounded-3xl bg-[#24BFA3] px-2 pb-2">
                  <Text className="mb-3 mt-4 text-center text-lg font-semibold text-white">
                    Current Cycle: {format(cycleStartCalendar, 'MMM dd, yyyy')}{' '}
                    - {format(cycleEndCalendar, 'MMM dd, yyyy')}
                  </Text>
                  <EarningCalendar
                    defaultRange={{
                      startDate: cycleStartCalendar,
                      endDate: cycleEndCalendar,
                    }}
                  />
                </Flex>

                <Flex justifyContent={'space-between'} px={2}>
                  <Flex className="my-3 flex-col text-center text-[#656565]">
                    <Text className="text-sm">Today’s Date</Text>
                    <Text className="text-xl font-semibold">{todayDate}</Text>
                  </Flex>

                  <Flex className="my-3 flex-col text-center text-[#656565]">
                    <Text className="text-sm">Cycle Start</Text>
                    <Text className="text-xl font-semibold">
                      {cycleStartDate}
                    </Text>
                  </Flex>

                  <Flex className="my-3 flex-col text-center text-[#656565]">
                    <Text className="text-sm">Cycle End</Text>
                    <Text className="text-xl font-semibold">
                      {cycleEndDate}
                    </Text>
                  </Flex>

                  <Flex className="my-3 flex-col text-center">
                    <Text className="text-sm text-[#656565]">Payday</Text>
                    <Text className="text-xl font-semibold text-[#24BFA3]">
                      {payDayDate}
                    </Text>
                  </Flex>
                </Flex>

                <Text
                  lineHeight={'normal'}
                  className="text-[16px] text-[#656565]"
                >
                  LYKSTAGE wallet statement closes on the 21st of every month,
                  which means one cycle is 22nd of the previous month to the
                  21st of the current month, settle on the 7th of the following month.
                </Text>
              </Flex>

              <Flex className="absolute bottom-[4%] w-full flex-col gap-3 px-4">
                <Button
                  className="w-full py-2 font-semibold"
                  color={'white'}
                  bg={'lyk-green'}
                  border="2px solid white"
                  _hover={{
                    border: '2px solid white',
                  }}
                  onClick={handleCloseMobileModal}
                >
                  Got it
                </Button>
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </>
  )
}
